import { motion } from "motion/react";
import { Icon } from "../../components/Icon";
import { heroSpring } from "../../motion";
import { CachedMedia } from "../../features/media/CachedMedia";
import type { Attachment } from "../../api/types";
import s from "./pieces.module.css";

interface ThumbProps {
  media: Attachment | null;
  /** Pairs the row thumb with the detail hero so one grows into the other. */
  layoutId?: string;
  onMediaError?: () => void;
  /** The row square by default; the grid card passes its own aspect box. */
  className?: string;
  iconSize?: 24 | 32;
}

/**
 * Retries, caching and the "this one really is gone" decision all live in
 * `CachedMedia`; a row only has to say what to draw in the meantime.
 */
export function Thumb({ media, layoutId, onMediaError, className, iconSize = 24 }: ThumbProps) {
  const isVideo = media?.mime_type.startsWith("video/") ?? false;
  // The badge marks a still as a clip. With no poster the fallback glyph is
  // already saying that, and two video marks in one 72px square is noise.
  const hasPoster = Boolean(media?.thumbnail_url || media?.display_url);

  return (
    <motion.div
      className={[s.thumb, className].filter(Boolean).join(" ")}
      layoutId={layoutId}
      transition={heroSpring}
    >
      <CachedMedia
        media={media}
        prefer="thumbnail"
        className={s.thumbImage}
        onUnavailable={onMediaError}
        // A clip that shipped without a poster — one uploaded before this app
        // made them, or one whose codec the WebView cannot decode — gets the
        // glyph for what it actually is, not a picture glyph on a video row.
        fallback={<Icon name={isVideo ? "video" : "image"} size={iconSize} />}
        brokenFallback={<Icon name={isVideo ? "video" : "noImage"} size={iconSize} />}
      />
      {isVideo && hasPoster && (
        <span className={s.thumbVideoMark}>
          <Icon name="video" size={16} />
        </span>
      )}
    </motion.div>
  );
}
